import { useEffect, useState } from 'react';
import socketService from './socketService';
import Events from './events';
import { fetchMemberLinkSocketToken } from 'services/memberMrn';

/**
 * Custom React hook that fetches the WebSocket authentication token
 * using `x-cooperate-id` from the funding source URL and initiates the socket connection.
 *
 * @param {string} fundingsourceId - The corporate Mongo ObjectId from the URL (e.g. 65fdca6c2d1ff51d733881aa)
 * @returns {{ isConnected: boolean, isConnecting: boolean, error: string|null, socketService: typeof socketService }}
 */
export function useFundingSourceSocket(fundingsourceId) {
  const [isConnected, setIsConnected] = useState(socketService.isConnected);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!fundingsourceId) {
      socketService.disconnectSocket();
      setIsConnected(false);
      return;
    }

    let isCancelled = false;

    async function initializeSocket() {
      setIsConnecting(true);
      setError(null);

      try {
        // 1. Fetch token and socket metadata from backend API using x-cooperate-id
        const responseData = await fetchMemberLinkSocketToken(fundingsourceId);

        if (isCancelled) return;

        // Support various token response formats safely
        const token =
          responseData?.token ||
          responseData?.data?.token ||
          responseData?.socketToken ||
          responseData?.jwt ||
          (typeof responseData === 'string' ? responseData : null);

        const serverUrl =
          responseData?.url ||
          responseData?.socketUrl ||
          responseData?.data?.url ||
          null;

        // 2. Connect the singleton socket service
        socketService.connectSocket({
          token: token || '',
          serverUrl,
          extraData: {
            cooperateId: fundingsourceId,
            ...(typeof responseData === 'object' ? responseData : {}),
          },
          extraHeaders: {
            'x-cooperate-id': fundingsourceId,
          },
        });
      } catch (err) {
        if (!isCancelled) {
          console.error('[useFundingSourceSocket] Error connecting socket:', err);
          setError(err?.message || 'Failed to authenticate socket');
        }
      } finally {
        if (!isCancelled) {
          setIsConnecting(false);
        }
      }
    }

    initializeSocket();

    // 3. Track connection status
    const unsubConnect = socketService.addListener(Events.CONNECT, () => {
      if (!isCancelled) setIsConnected(true);
    });

    const unsubDisconnect = socketService.addListener(Events.DISCONNECT, () => {
      if (!isCancelled) setIsConnected(false);
    });

    const unsubError = socketService.addListener(Events.CONNECT_ERROR, () => {
      if (!isCancelled) setIsConnected(false);
    });

    // 4. Teardown on unmount or when fundingsourceId changes
    return () => {
      isCancelled = true;
      unsubConnect();
      unsubDisconnect();
      unsubError();
      socketService.disconnectSocket();
      setIsConnected(false);
    };
  }, [fundingsourceId]);

  return {
    isConnected,
    isConnecting,
    error,
    socketService,
  };
}

export default useFundingSourceSocket;
